// Tegnforklaring + kontrollpanel for «Kritiske objekter» (J-5) og
// «Infrastruktur (OSM)». Svarer på spørsmålet operatøren faktisk har:
// HVA betyr symbolet, HVOR MANGE er det, og er laget PÅ?
//
// Hver rad viser NØYAKTIG samme APP-6-symbol som kartet tegner (PNG fra
// milsymbol), kategoriens farge, norsk etikett og antall — og er selv
// av/på-bryteren. Gjenbruker `.layer-row`-mønsteret fra sidebaren (prikk · navn ·
// antall · `.off`-klasse) og `.report-composer`-panelmalen, jf. UX_DESIGNSYSTEM
// §0/§4. Ingen window.prompt/alert.
import type { InfraKind, KritiskKategori } from '@skade/contracts';
import { INFRA_KINDS } from '@skade/contracts';

/** Alt panelet trenger å vite om de to lagene (ingen direkte kart-avhengighet). */
export interface KritiskeLegendHooks {
  /** J-5: antall per kategori, av/på-tilstand, symbol og farge. */
  koCounts: () => Record<KritiskKategori, number>;
  koIsOn: (kat: KritiskKategori) => boolean;
  koSetOn: (kat: KritiskKategori, on: boolean) => void;
  koSymbol: (kat: KritiskKategori) => string;
  koColor: (kat: KritiskKategori) => string;
  koLabel: (kat: KritiskKategori) => string;
  /** Antall synlige objekter i gjeldende kartutsnitt (0 ⇒ tilby «zoom hit»). */
  koInView: () => number;
  koFitNearest: () => number;

  /** OSM-infrastruktur (høyde-bærende) — eget under-lag. */
  infraCounts: () => Record<InfraKind, number>;
  infraIsOn: (k: InfraKind) => boolean;
  infraSetOn: (k: InfraKind, on: boolean) => void;
  infraSymbol: (k: InfraKind) => string;
  infraColor: (k: InfraKind) => string;
  infraLabel: (k: InfraKind) => string;
  infraStatusText: () => string;
  infraWithHeight: () => number;
  infraLoad: (force: boolean) => void;
}

const KATS: readonly KritiskKategori[] = ['MIL', 'EN', 'TR'];

export class KritiskePanel {
  readonly el: HTMLElement;
  private open = false;
  private koListEl!: HTMLElement;
  private koFootEl!: HTMLElement;
  private infraListEl!: HTMLElement;
  private infraFootEl!: HTMLElement;
  private hintEl!: HTMLElement;

  constructor(private hooks: KritiskeLegendHooks) {
    this.el = this.build();
    this.render();
  }

  isOpen(): boolean { return this.open; }

  /** Åpne panelet (idempotent) — brukes når operatøren slår på laget. */
  show() { if (!this.open) this.toggle(); else this.render(); }

  toggle(): boolean {
    this.open = !this.open;
    this.el.classList.toggle('open', this.open);
    if (this.open) this.render();
    return this.open;
  }

  private build(): HTMLElement {
    const p = div('report-composer kritiske-panel');
    const head = div('report-head');
    head.append(span('report-title', 'Kritiske objekter'));
    const x = button('report-x', '×', () => this.toggle());
    head.append(x);
    p.append(head);

    p.append(sectionLabel('J-5-kategorier'));
    this.koListEl = div('legend-list');
    p.append(this.koListEl);
    this.koFootEl = div('checklist-note');
    p.append(this.koFootEl);

    // «Jeg ser ingenting»-utveien: zoom til nærmeste objekter.
    this.hintEl = div('legend-hint');
    p.append(this.hintEl);

    const koRow = div('legend-btnrow');
    koRow.append(
      button('ko-btn', 'Vis alle', () => { for (const k of KATS) this.hooks.koSetOn(k, true); this.render(); }),
      button('ko-btn', 'Skjul alle', () => { for (const k of KATS) this.hooks.koSetOn(k, false); this.render(); }),
    );
    p.append(koRow);

    p.append(sectionLabel('Infrastruktur (OSM)'));
    this.infraListEl = div('legend-list');
    p.append(this.infraListEl);
    this.infraFootEl = div('checklist-note');
    p.append(this.infraFootEl);
    const infraRow = div('legend-btnrow');
    infraRow.append(
      button('ko-btn ko-btn-primary', 'Hent for utsnittet', () => this.hooks.infraLoad(true)),
      button('ko-btn', 'Skjul alle', () => { for (const k of INFRA_KINDS) this.hooks.infraSetOn(k, false); this.render(); }),
    );
    p.append(infraRow);
    return p;
  }

  /** Tegn hele panelet på nytt fra lagenes faktiske tilstand (ingen skyggekopi). */
  render() {
    if (!this.open) return;
    // ── J-5 ──
    const kc = this.hooks.koCounts();
    this.koListEl.innerHTML = '';
    let koTotal = 0;
    let koVisible = 0;
    for (const kat of KATS) {
      const on = this.hooks.koIsOn(kat);
      koTotal += kc[kat];
      if (on) koVisible += kc[kat];
      this.koListEl.append(this.legendRow({
        on, count: kc[kat], color: this.hooks.koColor(kat), label: this.hooks.koLabel(kat),
        symbol: this.hooks.koSymbol(kat), code: kat,
        onClick: () => { this.hooks.koSetOn(kat, !on); this.render(); },
      }));
    }
    this.koFootEl.textContent = `${koVisible} av ${koTotal} objekter synlige · kilde: VALE J-5 (statisk grunndata)`;

    // Tomtilstand-utvei: laget er på, men ingenting i utsnittet.
    this.hintEl.innerHTML = '';
    if (koVisible > 0 && this.hooks.koInView() === 0) {
      this.hintEl.append(span('legend-hint-t', 'Ingen av objektene er i kartutsnittet.'));
      this.hintEl.append(button('ko-btn', 'Zoom til nærmeste', () => { this.hooks.koFitNearest(); this.render(); }));
    }

    // ── OSM-infrastruktur ──
    const ic = this.hooks.infraCounts();
    this.infraListEl.innerHTML = '';
    let iTotal = 0;
    let iVisible = 0;
    for (const k of INFRA_KINDS) {
      const on = this.hooks.infraIsOn(k);
      iTotal += ic[k];
      if (on) iVisible += ic[k];
      this.infraListEl.append(this.legendRow({
        on, count: ic[k], color: this.hooks.infraColor(k), label: this.hooks.infraLabel(k),
        symbol: this.hooks.infraSymbol(k), code: '',
        onClick: () => { this.hooks.infraSetOn(k, !on); this.render(); },
      }));
    }
    this.infraFootEl.innerHTML = '';
    if (!iTotal) {
      // Tomtilstand med veiledning (UX_DESIGNSYSTEM §8) — ikke naken grå tekst.
      this.infraFootEl.append(span('legend-empty', this.hooks.infraStatusText()));
      this.infraFootEl.append(span('legend-note-line', 'Krever nett (Overpass). Master/tårn gir høyde til dekning og siktelinjer.'));
    } else {
      this.infraFootEl.append(span('legend-note-line',
        `${iVisible} av ${iTotal} synlige · ${this.hooks.infraWithHeight()} med oppgitt høyde`));
      this.infraFootEl.append(span('legend-note-line', this.hooks.infraStatusText()));
      this.infraFootEl.append(span('legend-note-line', 'Høyde (m over bakken) mater dekning/siktelinjer. © OpenStreetMap-bidragsytere (ODbL).'));
    }
  }

  /** Én tegnforklaringsrad: APP-6-symbol · farge · etikett · antall · av/på. */
  private legendRow(o: {
    on: boolean; count: number; color: string; label: string; symbol: string; code: string;
    onClick: () => void;
  }): HTMLElement {
    const row = document.createElement('button');
    row.type = 'button';
    row.className = o.on ? 'layer-row legend-row' : 'layer-row legend-row off';
    row.setAttribute('aria-pressed', String(o.on));
    row.title = o.on ? `${o.label} — vises (klikk for å skjule)` : `${o.label} — skjult (klikk for å vise)`;

    const sym = div('legend-sym');
    if (o.symbol) {
      const img = document.createElement('img');
      img.src = o.symbol; img.alt = ''; img.width = 22; img.height = 22;
      sym.append(img);
    } else {
      // Uten canvas (server-render/test): fall til fargeprikk som i sidebaren.
      const dot = div('dot');
      dot.style.background = o.color;
      sym.append(dot);
    }
    row.append(sym);

    const name = span('name', o.code ? `${o.label} (${o.code})` : o.label);
    name.style.color = o.on ? o.color : '';
    row.append(name, span('count', String(o.count)), span('toggle', o.on ? '' : ''));
    row.onclick = o.onClick;
    return row;
  }
}

function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
function sectionLabel(text: string): HTMLElement { return span('section-label', text); }
function button(cls: string, text: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button'; b.className = cls; b.textContent = text; b.onclick = onClick;
  return b;
}
